/**
 * Credit request — Shopify Forms enhancements.
 *
 * 1. Required markers: the Forms markup carries no `required` attributes, but
 *    the app's own config request (form_instances) lists a `required`
 *    validator per field. snippets/shopify-forms-styles.liquid captures that
 *    response into window.OFWMFormsConfig; labels/legends of required fields
 *    get `data-ofwm-required` (a data attribute survives React re-renders,
 *    a class would not) and the stylesheet appends the asterisk.
 *
 * 2. Product rows:
 *
 * Shopify Forms has no repeating fields, so the "Products requiring a credit"
 * multi-line field is driven by a small row editor (stock item / description /
 * quantity) injected next to it inside the Forms shadow root. Every edit
 * serialises the rows into the real textarea through the native value setter +
 * an `input` event, which is how React-controlled inputs pick up external
 * changes, so the app's state, validation and submission all see the text.
 *
 * Only our own nodes are added; the app's nodes are never moved or removed
 * (Forms inserts validation messages as siblings, so reparenting would break it).
 */
(() => {
  const FIELD = 'custom#products_requiring_a_credit';
  const MAX_ROWS = 15;
  const INITIAL_ROWS = 3;
  const COLUMNS = [
    { key: 'stock item', placeholder: 'Stock item' },
    { key: 'description', placeholder: 'Description' },
    { key: 'quantity', placeholder: 'Qty' },
  ];

  const nativeSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
  const setValue = (textarea, value) => {
    if (textarea.value === value) return;
    nativeSetter.call(textarea, value);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const build = (textarea) => {
    const container = textarea.parentElement;
    if (!container || container.querySelector('.ofwm-products')) return;
    const doc = container.ownerDocument;

    const widget = doc.createElement('div');
    widget.className = 'ofwm-products';
    widget.innerHTML =
      '<div class="ofwm-products__head" aria-hidden="true">' +
        COLUMNS.map((c) => `<span>${c.placeholder.replace(/ \(.*\)$/, '')}</span>`).join('') +
        '<span></span></div>' +
      '<div class="ofwm-products__rows" role="group" aria-label="Products requiring a credit"></div>' +
      '<button type="button" class="ofwm-products__add">Add another product</button>';

    const rows = widget.querySelector('.ofwm-products__rows');
    const addButton = widget.querySelector('.ofwm-products__add');

    const serialise = () => {
      const lines = Array.from(rows.children).map((row) => {
        const values = Array.from(row.querySelectorAll('input')).map((input) => input.value.trim());
        return values.some(Boolean) ? values.join(' | ') : null;
      }).filter(Boolean);
      setValue(textarea, lines.join('\n'));
    };

    const renumber = () => {
      const list = Array.from(rows.children);
      list.forEach((row, index) => {
        row.querySelectorAll('input').forEach((input) => {
          input.setAttribute('aria-label', `Product ${index + 1} ${input.dataset.field}`);
        });
        row.querySelector('button').style.visibility = list.length > 1 ? 'visible' : 'hidden';
      });
      addButton.disabled = list.length >= MAX_ROWS;
    };

    const addRow = () => {
      if (rows.children.length >= MAX_ROWS) return;
      const row = doc.createElement('div');
      row.className = 'ofwm-products__row';
      row.innerHTML =
        COLUMNS.map((c) => `<input type="text" data-field="${c.key}" placeholder="${c.placeholder}" autocomplete="off">`).join('') +
        '<button type="button" class="ofwm-products__remove" aria-label="Remove product row">&times;</button>';
      rows.appendChild(row);
      renumber();
    };

    rows.addEventListener('input', serialise);
    rows.addEventListener('click', (event) => {
      const remove = event.target.closest('.ofwm-products__remove');
      if (!remove) return;
      remove.closest('.ofwm-products__row').remove();
      renumber();
      serialise();
    });
    addButton.addEventListener('click', addRow);
    // Enter inside a row should not submit the form
    widget.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.tagName === 'INPUT') event.preventDefault();
    });

    for (let i = 0; i < INITIAL_ROWS; i++) addRow();
    container.classList.add('ofwm-products-host');
    container.appendChild(widget);
  };

  /* ---- Required markers ---- */
  const formIdOf = (root) => {
    const owner = root.host && root.host.closest('[data-forms-id]');
    const match = owner && /forms-root-(\d+)/.exec(owner.getAttribute('data-forms-id'));
    return match ? match[1] : null;
  };

  const requiredNames = (root) => {
    const config = window.OFWMFormsConfig;
    if (!Array.isArray(config)) return null;
    const id = formIdOf(root);
    const form = config.find((f) => String(f.id) === id) || config[0];
    const definition = form && form.form_definition;
    const fields = definition && ((definition.form && definition.form.fields) || definition.fields);
    if (!fields) return null;
    return fields
      .filter((f) => Array.isArray(f.validators) && f.validators.some((v) => v.name === 'required'))
      .map((f) => f.name);
  };

  const markRequired = (root) => {
    const names = requiredNames(root);
    if (!names) return;
    names.forEach((name) => {
      root.querySelectorAll(`label[for="${name}"], fieldset[name="${name}"] > legend, [name="${name}"], [data-testid="field-${name}"]`)
        .forEach((el) => { if (!el.hasAttribute('data-ofwm-required')) el.setAttribute('data-ofwm-required', 'true'); });
    });
  };

  const ensureConfig = () => {
    if (window.OFWMFormsConfig || window.OFWMFormsConfigRequested) return;
    window.OFWMFormsConfigRequested = true;
    // Fallback if the fetch wrapper missed the app's request: ask for the same public config
    const shop = window.Shopify && window.Shopify.shop;
    if (!shop) return;
    fetch(`https://forms.shopifyapps.com/api/v2/form_instances?shopify_domain=${encodeURIComponent(shop)}`)
      .then((r) => r.json())
      .then((json) => { window.OFWMFormsConfig = json; document.dispatchEvent(new CustomEvent('ofwm:forms-config')); })
      .catch(() => {});
  };

  const scan = (root) => {
    const textarea = root.querySelector(`textarea[name="${FIELD}"]`);
    if (textarea) build(textarea);
    if (root.querySelector('form')) {
      markRequired(root);
      if (!window.OFWMFormsConfig) setTimeout(ensureConfig, 3000);
    }
  };

  const watchHost = (host) => {
    if (host.dataset.ofwmProducts || !host.shadowRoot) return;
    host.dataset.ofwmProducts = 'true';
    const root = host.shadowRoot;
    scan(root);
    // The form (re)renders asynchronously — keep watching for the field
    new MutationObserver(() => scan(root)).observe(root, { childList: true, subtree: true });
    document.addEventListener('ofwm:forms-config', () => scan(root));
  };

  const findHosts = () => {
    const hosts = document.querySelectorAll('shopify-forms-embed');
    hosts.forEach(watchHost);
    return hosts.length > 0;
  };

  if (!findHosts()) {
    const observer = new MutationObserver(() => {
      if (findHosts()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
